import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { liteClient } from 'algoliasearch/lite';
import { renderToString } from 'react-dom/server';
import { InstantSearchSSRProvider, InstantSearch, Configure, getServerState } from 'react-instantsearch';
export { InstantSearch } from 'react-instantsearch';
import { useAlgoliaConfig } from './AlgoliaConfig.js';
import { AlgoliaConfigProvider } from './AlgoliaConfigProvider.js';
import { AlgoliaDataProvider } from './AlgoliaDataProvider.js';
import VirtualFacets from './components/VirtualFacets/VirtualFacets.js';
import { parseCommercialFacets } from './utils/parse-commercial-facets.js';

const AlgoliaSearch = ({ children  })=>{
    const { algoliaInstantSearchProps: { searchClient , indexName , onStateChange , resultsState , searchState ,  } , priceSegmentCookie , facetingAfterDistinct ,  } = useAlgoliaConfig();
    return /*#__PURE__*/ React.createElement(InstantSearchSSRProvider, {
        initialResults: resultsState
    }, /*#__PURE__*/ React.createElement(InstantSearch, {
        searchClient: searchClient,
        indexName: indexName,
        onStateChange: onStateChange,
        initialUiState: searchState,
        future: {
            // In order to save refinements between desktop/mobile, we need to preserve
            // state when unmounting algolia hooks (otherwise state gets reset)
            // https://www.algolia.com/doc/guides/building-search-ui/widgets/show-and-hide-widgets/react/?client=App.jsx
            preserveSharedStateOnUnmount: true
        }
    }, /*#__PURE__*/ React.createElement(VirtualFacets, null), /*#__PURE__*/ React.createElement(Configure, {
        clickAnalytics: true,
        facetingAfterDistinct: facetingAfterDistinct
    }), /*#__PURE__*/ React.createElement(AlgoliaDataProvider, {
        priceSegmentCookie: priceSegmentCookie
    }, children)));
};
function fetchAlgoliaConfig(props) {
    return _fetchAlgoliaConfig.apply(this, arguments);
}
function _fetchAlgoliaConfig() {
    _fetchAlgoliaConfig = swcHelpers.asyncToGenerator(function*(props) {
        if (!props.isEnabled) {
            return props;
        }
        const { appId , apiKey  } = props.options;
        const searchClient = liteClient(appId, apiKey);
        try {
            var ref;
            /**
     * Algolia SDK does not easily expose all the facets in the resultsState, so we have to
     * manually fetch all the facets and integrate them to the existing ones coming from next.config.
     * The alternative is to get from rawResults (https://github.com/algolia/instantsearch/issues/6202)
     * but since we need to have access to all facets before we SSR, we need to fetch all the facets here:
     */ const prefetchFacetsResult = yield searchClient.search({
                requests: [
                    {
                        indexName: props.options.indexName,
                        query: '',
                        facets: [
                            '*'
                        ]
                    }
                ]
            });
            var ref1;
            const rawFacets = (ref1 = (ref = prefetchFacetsResult.results[0]) === null || ref === void 0 ? void 0 : ref.facets) !== null && ref1 !== void 0 ? ref1 : {};
            const commercialFacets = parseCommercialFacets({
                rawFacets
            });
            const combinedProps = swcHelpers.objectSpread({}, props, {
                searchClient,
                options: swcHelpers.objectSpread({}, props.options, {
                    facets: [
                        ...props.options.facets,
                        ...commercialFacets
                    ]
                })
            });
            return swcHelpers.objectSpread({}, combinedProps, {
                resultsState: (yield getServerState(/*#__PURE__*/ React.createElement(AlgoliaConfigProvider, swcHelpers.extends({}, combinedProps), /*#__PURE__*/ React.createElement(InstantSearchContainer, null, /*#__PURE__*/ React.createElement(VirtualFacets, null))), {
                    renderToString
                })).initialResults
            });
        } catch (err) {
            console.error('An error occurred while fetching algolia hits:', err);
            return swcHelpers.objectSpread({}, props, {
                isEnabled: false
            });
        }
    });
    return _fetchAlgoliaConfig.apply(this, arguments);
}
const InstantSearchContainer = ({ children  })=>{
    const { algoliaInstantSearchProps: { searchClient , indexName , onStateChange , searchState , resultsState ,  } ,  } = useAlgoliaConfig();
    return /*#__PURE__*/ React.createElement(InstantSearchSSRProvider, {
        initialResults: resultsState
    }, /*#__PURE__*/ React.createElement(InstantSearch, {
        searchClient: searchClient,
        indexName: indexName,
        onStateChange: onStateChange,
        initialUiState: searchState
    }, children));
};

export { AlgoliaSearch, fetchAlgoliaConfig };
//# sourceMappingURL=AlgoliaSearch.js.map
